import React, { Suspense } from 'react';

// @ts-expect-error - JSX file without TypeScript declarations
import { EventForm, FormSkeleton, OverlayElement } from 'donation-elements';
import HostedPagePreviewFrame from './HostedPagePreviewFrame';
import FormPreviewWorkspaceShell from '../../components/workspaces/forms/FormPreviewWorkspaceShell';
// @ts-expect-error Legacy JavaScript utility has no declaration file yet.
import { reconcileEventTicketPreviewDefinitions } from '../../utils/eventTicketAuthoring';

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(' ');
}

const previewTabs = [
  { id: 'embed_form', name: 'Embed Form', current: true },
  { id: 'hosted_page', name: 'Hosted Page', current: false },
  { id: 'overlay', name: 'Overlay', current: false },
];

interface CustomTicket {
  id?: string;
  name?: string;
  price?: number;
  type?: string;
  seats?: number;
}

interface CustomFAQ {
  question: string;
  answer: string;
}

interface EventFormPreviewProps {
  formName?: string;
  image?: string;
  color?: string;
  convertedContent?: string;
  customTickets?: CustomTicket[];
  ticketContractVersion?: number;
  customFAQs?: CustomFAQ[];
  activePreviewTab: string;
  setActivePreviewTab: (tab: string) => void;
  merchantName?: string;
  elementID?: string;
  eventStartDate?: string;
  eventEndDate?: string;
  eventStartTime?: string;
  eventEndTime?: string;
  eventLocation?: string;
  timeZone?: string;
  enableTimeAndLocation?: boolean;
  enableEmbedForm?: boolean;
  customInputs?: any[];
  showFundraisingBar?: boolean;
  targetAmount?: string;
  amountOffset?: string;
  countOffset?: string;
  showAnonymousDonations?: boolean;
  showOrganizationNameField?: boolean;
  active?: boolean;
  formType?: string;
  studioMode?: boolean;
  isExpanded?: boolean;
  onToggleExpanded?: () => void;
}

// Event Form Preview Section Component
const EventFormPreview: React.FC<EventFormPreviewProps> = ({
  formName,
  image,
  color,
  convertedContent,
  customTickets,
  ticketContractVersion,
  customFAQs,
  activePreviewTab,
  setActivePreviewTab,
  merchantName,
  elementID,
  eventStartDate,
  eventEndDate,
  eventStartTime,
  eventEndTime,
  eventLocation,
  timeZone,
  enableTimeAndLocation,
  enableEmbedForm,
  customInputs,
  showFundraisingBar,
  targetAmount,
  amountOffset,
  countOffset,
  showAnonymousDonations,
  showOrganizationNameField,
  active,
  formType,
  studioMode = false,
  isExpanded = false,
  onToggleExpanded,
}) => {
  const lastValidTicketsRef = React.useRef<CustomTicket[]>([]);
  const previewTickets = React.useMemo(() => {
    const resolved = reconcileEventTicketPreviewDefinitions(
      customTickets || [],
      lastValidTicketsRef.current,
    );
    lastValidTicketsRef.current = resolved;
    return resolved;
  }, [customTickets]);

  // Check if we have valid data to render
  // For new forms (no elementID, 'preview', or 'NEW_FORM'), always render immediately with defaults
  // For existing forms (valid UUID), wait until elementID and formName are loaded from backend
  const isNewForm = !elementID || elementID === 'preview' || elementID === 'NEW_FORM';
  const isDataReady = isNewForm || (elementID && formName);

  // Create preview data object that matches the EventForm data structure
  const eventFormPreviewData = React.useMemo(() => {
    // Always return data, even without elementID (for new form previews)
    return {
      elementID: elementID || 'preview',
      elementName: formName || 'Enter Event Title',
      formName: formName || 'Enter Event Title',
      elementType: formType || 'EVENT',
      formType: formType || 'EVENT',
      lightColor: color || '#9A9285',
      darkColor: color || '#9A9285',
      nonprofitName: merchantName || 'Your Organization',
      image: image || null,
      eventDetails: convertedContent || '',
      details: convertedContent || '',
      eventStartDate: eventStartDate || '',
      eventEndDate: eventEndDate || '',
      eventStartTime: eventStartTime || '',
      eventEndTime: eventEndTime || '',
      eventLocation: eventLocation || '',
      timeZone: timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone,
      enableTimeAndLocation: enableTimeAndLocation ?? true,
      enableEmbedForm: enableEmbedForm ?? true,
      ticketContractVersion: ticketContractVersion ?? 2,
      tickets: previewTickets,
      customInputs: customInputs || [],
      formFaqs: customFAQs || [],
      showAnonymousDonations: showAnonymousDonations ?? false,
      showOrganizationNameField: showOrganizationNameField ?? false,
      active: active ?? true,
      showFundraisingBar: showFundraisingBar ?? false,
      targetAmount: targetAmount || '0',
      amountOffset: amountOffset || '0',
      countOffset: countOffset || '0',
      // Additional required fields for StandardForm compatibility
      funds: [],
      frequencies: ['ONE_TIME'],
      defaultFrequency: 'ONE_TIME',
      oneTimeAmounts: [25, 50, 100, 250],
      monthlyAmounts: [],
      weeklyAmounts: [],
      dailyAmounts: [],
      annuallyAmounts: [],
      defaultOneTimeAmount: 50,
      defaultDailyAmount: 0,
      defaultWeeklyAmount: 0,
      defaultMonthlyAmount: 0,
      defaultAnnuallyAmount: 0,
      showScheduleDonation: false,
      cardMin: 1,
      defaultPaymentProcessor: 'STRIPE',
      maxTransactionAmount: 999999.99,
      showAddToCart: false,
    };
  }, [
    elementID,
    formName,
    color,
    image,
    merchantName,
    convertedContent,
    eventStartDate,
    eventEndDate,
    eventStartTime,
    eventEndTime,
    eventLocation,
    timeZone,
    enableTimeAndLocation,
    enableEmbedForm,
    ticketContractVersion,
    previewTickets,
    customInputs,
    customFAQs,
    showAnonymousDonations,
    showOrganizationNameField,
    active,
    showFundraisingBar,
    targetAmount,
    amountOffset,
    countOffset,
    formType,
  ]);

  const previewContent =
    activePreviewTab === 'Embed Form' ? (
      <Suspense fallback={<div>Loading event form...</div>}>
        <div
          data-cs-position="donor"
          // CON-42: donor components mounted in the dashboard's document resolve every
          // --cs-* against dashboard.css unless a scope says otherwise. Applies
          // donor-scope.css to this subtree only; the dashboard's own chrome is
          // untouched. Content a donor component portals to document.body escapes
          // this — see decisions.md D-previewer-scope.
          className={studioMode ? 'py-3' : 'py-4'}
          style={{
            zoom: 0.7, // Adjust this value (0.5 = 50%, 0.6 = 60%, etc.)
            maxWidth: '100%',
          }}
        >
          {/* Only render EventForm when we have valid data (elementID + formName) to prevent flicker */}
          {/* This ensures all form data has been populated before rendering */}
          {isDataReady ? (
            <EventForm
              formID={elementID}
              brandColors={color}
              isDrawerOpen={false}
              backendUrl={import.meta.env.VITE_ELEMENTS_BACKEND_URL}
              previewMode={true}
              previewData={eventFormPreviewData}
            />
          ) : (
            <FormSkeleton formType={formType || 'EVENT'} brandColors={color} isDrawerOpen={false} />
          )}
        </div>
      </Suspense>
    ) : activePreviewTab === 'Overlay' ? (
      <Suspense fallback={<div>Loading overlay preview...</div>}>
        <div
          data-cs-position="donor"
          // CON-42: donor components mounted in the dashboard's document resolve every
          // --cs-* against dashboard.css unless a scope says otherwise. Applies
          // donor-scope.css to this subtree only; the dashboard's own chrome is
          // untouched. Content a donor component portals to document.body escapes
          // this — see decisions.md D-previewer-scope.
          style={{
            zoom: 0.55,
            maxWidth: '100%',
          }}
        >
          {isDataReady ? (
            <OverlayElement
              elementID={elementID || 'preview'}
              backendUrl={import.meta.env.VITE_ELEMENTS_BACKEND_URL}
              previewMode={true}
              previewData={eventFormPreviewData}
            />
          ) : (
            <FormSkeleton formType={formType || 'EVENT'} brandColors={color} isDrawerOpen={false} />
          )}
        </div>
      </Suspense>
    ) : (
      <HostedPagePreviewFrame
        previewData={{
          formName,
          image,
          color,
          convertedContent,
          customTickets: previewTickets,
          ticketContractVersion: ticketContractVersion ?? 2,
          customFAQs,
          customInputs,
          merchantName,
          elementID,
          eventStartDate,
          eventEndDate,
          eventStartTime,
          eventEndTime,
          eventLocation,
          timeZone,
          showFundraisingBar,
          targetAmount,
          amountOffset,
          countOffset,
          enableTimeAndLocation,
          enableEmbedForm,
          showAnonymousDonations,
          showOrganizationNameField,
          active,
          formType,
        }}
      />
    );

  if (studioMode) {
    return (
      <FormPreviewWorkspaceShell
        tabs={previewTabs}
        activeTab={activePreviewTab}
        onTabChange={setActivePreviewTab}
        isExpanded={isExpanded}
        onToggleExpanded={onToggleExpanded}
      >
        {previewContent}
      </FormPreviewWorkspaceShell>
    );
  }

  return (
    <div className="flex h-full flex-1 flex-col bg-paper-muted">
      <div className="m-4 rounded-lg border border-edge bg-paper-inset">
        <nav className="flex w-full p-0.5" aria-label="Preview mode">
          {previewTabs.map((tab) => (
            <button
              type="button"
              key={tab.name}
              onClick={() => setActivePreviewTab(tab.name)}
              aria-pressed={tab.name === activePreviewTab}
              className={classNames(
                tab.name === activePreviewTab
                  ? 'border border-edge bg-white text-ink'
                  : 'border border-transparent bg-transparent text-ink-muted hover:text-ink-secondary',
                'flex-1 whitespace-nowrap rounded-md p-1 text-sm font-medium focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0',
              )}
            >
              {tab.name}
            </button>
          ))}
        </nav>
      </div>
      <div className="h-full w-full flex-1 overflow-y-hidden">{previewContent}</div>
    </div>
  );
};

export default EventFormPreview;
